import React from 'react';
import { useTranslation } from 'react-i18next';
import { CustomerAttributeFieldDTO } from '../services/customerAttributeService';

interface Props {
  title: string;
  fields: CustomerAttributeFieldDTO[];
  values: Record<number, string>;
  onChange: (attributeDefinitionId: number, value: string) => void;
  errors?: Record<number, string>;
}

/**
 * Renders an app-scoped custom-attribute block on a Customer form (e.g. Car
 * Rental's Driver Details: NIC No, Driving Licence No, Issued/Expiry dates).
 * Purely data-driven off CustomerAttributesDTO — no hardcoded field list, so a
 * different app's seeded attributes render here unchanged.
 */
const CustomerAttributeFields: React.FC<Props> = ({ title, fields, values, onChange, errors }) => {
  const { t } = useTranslation();
  if (fields.length === 0) return null;

  const inputType = (dataType: string) => {
    switch (dataType) {
      case 'Date': return 'date';
      case 'Number': case 'Decimal': return 'number';
      default: return 'text';
    }
  };

  return (
    <div className="ims-card" style={{ marginTop: '1rem' }}>
      <div className="ims-card-header">
        <span className="ims-card-title">{title}</span>
      </div>
      <div className="ims-card-body">
        {fields.map(f => (
          <div className="form-group" key={f.attributeDefinitionId}>
            <label className="form-label">
              {f.name} {f.isRequired && <span className="required">*</span>}
            </label>
            {f.dataType === 'Select' ? (
              <select
                className={`form-select ${errors?.[f.attributeDefinitionId] ? 'is-invalid' : ''}`}
                value={values[f.attributeDefinitionId] ?? ''}
                onChange={e => onChange(f.attributeDefinitionId, e.target.value)}
              >
                <option value="">{t('common.select', 'Select...')}</option>
                {(f.options ?? []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input
                type={inputType(f.dataType)}
                className={`form-input ${errors?.[f.attributeDefinitionId] ? 'is-invalid' : ''}`}
                value={values[f.attributeDefinitionId] ?? ''}
                onChange={e => onChange(f.attributeDefinitionId, e.target.value)}
                maxLength={500}
              />
            )}
            {errors?.[f.attributeDefinitionId] && (
              <small className="form-error">{errors[f.attributeDefinitionId]}</small>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerAttributeFields;
